<template>
  <div class="flex items-center justify-end py-2 gap-2 bg-base-100">
    <div class="flex flex-col gap-2 w-full">
      <button
        class="btn btn-primary rounded btn-sm"
        @click="startCreateNewProps(Number(moduleId))"
      >
        <i class="iconify mingcute--plus-fill text-[10px]" />
        <span class="text-xs">{{
          $t(
            moduleName === "relationship"
              ? "User.mate.relation.custom"
              : `User.mate.attribute.createCustom`
          )
        }}</span>
      </button>
      <div
        class="flex items-center gap-2"
        v-if="moduleStore.module?.canExportModule(moduleId) && enableTemplate"
      >
        <button
          class="btn btn-outline btn-sm flex-1 whitespace-nowrap"
          @click="clickCreateTemplate"
        >
          <i class="iconify mingcute--save-2-line text-[10px]" />
          <span class="text-xs">{{ $t("Message.createTemplate") }}</span>
        </button>

        <van-popover v-model:show="showDraggingTips" placement="top">
          <div class="text-xs p-2">
            {{ $t(`Message.dragModuleToSort`) }}
          </div>
          <template #reference>
            <button
              :class="[
                'btn btn-sm flex-1 whitespace-nowrap btn-outline',
                isDraggingMode ? 'text-primary' : '',
              ]"
              @click="
                emit('update:is-dragging-mode', !isDraggingMode);
                showDraggingTips = isDraggingMode;
              "
            >
              <i class="iconify mingcute--edit-3-line text-[10px]" />
              <span class="text-xs">{{ $t(`collection.mode.edit`) }}</span>
            </button>
          </template>
        </van-popover>
      </div>

      <van-popover v-else v-model:show="showDraggingTips" placement="top">
        <div class="text-xs p-2">
          {{ $t(`Message.dragModuleToSort`) }}
        </div>
        <template #reference>
          <button
            :class="[
              'btn btn-sm  whitespace-nowrap btn-outline w-full',
              isDraggingMode ? 'text-primary' : '',
            ]"
            @click="
              emit('update:is-dragging-mode', !isDraggingMode);
              showDraggingTips = isDraggingMode;
            "
          >
            <i class="iconify mingcute--edit-3-line text-[10px]" />
            <span class="text-xs">{{ $t(`collection.mode.edit`) }}</span>
          </button>
        </template>
      </van-popover>
    </div>
    <VanPopup
      v-model:show="showCreateProp"
      position="bottom"
      @close="newPropsName = ''"
    >
      <div class="flex flex-col gap-2">
        <div
          class="flex justify-between items-center border-b border-b-secondary/10 p-4"
        >
          <span>{{
            $t("Buttons.createCustom", {
              name: $t(`User.mate.module.${moduleName}`),
            })
          }}</span>
          <button
            :disabled="newPropsName === ''"
            class="btn btn-sm btn-primary"
            @click="confirmCreateProp"
          >
            {{ $t("Buttons.sure") }}
          </button>
        </div>
        <div class="p-4 w-full">
          <input
            v-model="newPropsName"
            :placeholder="$t('User.mate.attribute.name')"
            class="input input-sm w-full rounded"
          />
        </div>
      </div>
    </VanPopup>
  </div>
</template>

<script setup lang="ts">
import { showLoadingToast, showFailToast, showSuccessToast } from "vant";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useModule } from "@/stores/module";
import { useGlobalModal } from "@/stores/globalModal";
import { RELATION_MODULE_ID } from "@/stores/module/moduleMate";

const props = defineProps({
  moduleId: {
    type: Number,
    required: true,
  },
  moduleName: {
    type: String,
    default: "prop",
  },
  isDraggingMode: {
    type: Boolean,
    default: false,
  },
  enableDragging: {
    type: Boolean,
    default: false,
  },
  enableTemplate: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  "update:is-dragging-mode": [boolean];
}>();
const { t } = useI18n();
const moduleStore = useModule();
const showCreateProp = ref<boolean>(false);
const newPropsModuleId = ref<number>(0);
const newPropsName = ref<string>("");

const startCreateNewProps = (moduleId: number) => {
  newPropsModuleId.value = moduleId;
  showCreateProp.value = true;
};
const confirmCreateProp = async () => {
  const toast = showLoadingToast({
    message: t("Message.creatingAttribute"),
    duration: 0,
  });
  try {
    const questionId = await moduleStore.createQuestion(
      newPropsModuleId.value,
      newPropsName.value,
      props.moduleId === RELATION_MODULE_ID ? 6 : undefined,
      props.moduleId === RELATION_MODULE_ID ? "mate" : undefined
    );
    await moduleStore.module!.addMateModuleQuestion(
      newPropsModuleId.value,
      questionId
    );
    showCreateProp.value = false;
    newPropsName.value = "";
    newPropsModuleId.value = 0;
  } catch (e) {
    showFailToast(t("Message.failCreatingAttribute"));
    throw e;
  } finally {
    toast.close();
  }
};

const showDraggingTips = ref<boolean>(false);

// 创建模板
const clickCreateTemplate = async () => {
  useGlobalModal().openTextInputModal({
    title: t("Message.createTemplateTitle"),
    callback: async (name?: string) => {
      if (!name) return;
      const toast = showLoadingToast({
        message: t("Message.creating"),
        duration: 0,
      });
      try {
        await moduleStore.createTemplate(props.moduleId, name);
        toast.close();
        showSuccessToast(t("Message.saveSuccess"));
      } catch (e) {
        showFailToast(t("Message.saveFailed"));
        toast.close();
        throw e;
      }
    },
  });
};

// top=65+15
// right=60
const offset = ref({ x: 315, y: 80 });

onMounted(() => {
  initOffset();
  window.addEventListener("resize", () => {
    initOffset();
  });
});

const initOffset = () => {
  const screenWidth = window.innerWidth;
  if (screenWidth >= 1024) {
    offset.value.x = screenWidth / 2 + 225;
  } else {
    offset.value.x = screenWidth - 72;
  }
};
</script>
